/**
 * หน้าจอความล้มเหลวของการรับเข้าสต๊อก — หนึ่งกฎ หนึ่งข้อความ
 *
 * ทุกกรณีที่ `docs/API_CONTRACT.md` นิยามไว้ต้องอ่านออกว่าเกิดอะไรและ **ใครเป็น
 * ผู้แก้** ห้ามยุบเป็น "บันทึกไม่สำเร็จ" ก้อนเดียว เพราะสองข้อที่หน้าตาเหมือนกัน
 * ที่สุดในชุดนี้ต้องการคนละแผนก:
 *
 * - `NO_UNIT_COST` — สินค้าตัวนี้ยังไม่มีต้นทุนมาตรฐานในโรงงานนี้ ผู้ดูแลข้อมูล
 *   สินค้า/ต้นทุนเป็นผู้แก้
 * - `NO_ACCOUNT_MAPPING` — ผังบัญชีของ movement type นี้ยังไม่ถูกตั้ง ฝ่ายบัญชี
 *   เป็นผู้แก้
 *
 * และ `FISCAL_PERIOD_CLOSED` ไม่ใช่ทั้งสองอย่าง — มันคือ "งวดปิดไปแล้ว" ซึ่งแก้
 * ด้วยการเปลี่ยนวันที่ที่มีผล หรือด้วยการที่ฝ่ายบัญชีเปิดงวด ไม่ใช่ด้วยการกดซ้ำ
 *
 * `409` ของกลุ่มนี้มีความหมายเดียวคือ **`IDEMPOTENCY_KEY_REUSED`** — คีย์เดิมถูก
 * ใช้กับคำขอที่ต่างออกไป มันคือ bug ของ client ไม่ใช่ผู้ใช้สองคนชนกัน และห้าม
 * กลืนเด็ดขาด: การกลืนมันคือการทิ้งของจริงที่เพิ่งมาถึงไปเงียบ ๆ
 *
 * `403` ของกลุ่มนี้ **ไม่ใช่** "ทำรายการไม่สำเร็จ" แต่คือ "โรงงานนี้อยู่นอกขอบเขต
 * ของคุณ" (D-004 — contract ระบุว่านอก scope ตอบ `403` ไม่ใช่ `404`)
 */

import { StatusBadge } from '../StatusBadge';
import {
  ruleFromFailure,
  type ApiErrorEnvelope,
  type GoodsReceiptFailure,
} from '../../lib/goodsReceipts/client';
import {
  FISCAL_PERIOD_CLOSED,
  IDEMPOTENCY_KEY_REUSED,
  NO_ACCOUNT_MAPPING,
  NO_UNIT_COST,
  PRODUCT_NOT_ACTIVE,
  STORAGE_LOCATION_NOT_IN_PLANT,
} from '../../lib/goodsReceipts/types';

export type GoodsReceiptFailureContext =
  /** อ่านรายการใบรับเข้า */
  | 'list'
  /** อ่านใบรับเข้าหนึ่งใบ */
  | 'detail'
  /** บันทึกการรับเข้า */
  | 'create';

export interface GoodsReceiptFailureNoticeProps {
  readonly failure: GoodsReceiptFailure;
  readonly context: GoodsReceiptFailureContext;
  /** โรงงานที่คำขอนี้อ้างถึง — `403` อ่านรู้เรื่องขึ้นมากเมื่อบอกว่าโรงงานไหน */
  readonly plantCode?: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: GoodsReceiptFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความของกฎทางธุรกิจแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * แยกออกมาจาก `failureCopy()` เพราะกฎเหล่านี้มาพร้อม `422`/`409` แต่ **ความหมาย
 * ของมันไม่ใช่รหัส HTTP** มันคือคำอธิบายว่าต้องแก้อะไรและใครแก้ ซึ่งเป็นสิ่งเดียว
 * ที่ผู้ใช้ต้องอ่าน
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case NO_UNIT_COST:
      return {
        heading: 'สินค้าตัวนี้ยังไม่มีต้นทุนต่อหน่วยในโรงงานนี้ จึงตีมูลค่าไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_UNIT_COST} — การรับเข้าต้องสร้างเอกสารบัญชีที่มีมูลค่า และมูลค่ามาจาก จำนวน × unit_cost ของสินค้ากับโรงงานคู่นี้ เมื่อยังไม่มีค่านั้น ระบบจะไม่โพสต์ด้วยมูลค่าศูนย์ เพราะเอกสารบัญชีที่มูลค่าศูนย์คือของที่หายไปจากงบ — ให้ผู้ดูแลข้อมูลสินค้าตั้ง unit_cost ของสินค้าในโรงงานนี้ก่อน ยังไม่มีของเข้าสต๊อกและยังไม่มี lot ถูกสร้าง`,
      };
    case NO_ACCOUNT_MAPPING:
      return {
        heading: 'ยังไม่มีการผูกผังบัญชีสำหรับการรับเข้าของบริษัทนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_ACCOUNT_MAPPING} — บัญชีเดบิต/เครดิตของการรับเข้ามาจากตารางผูก movement type กับผังบัญชี และระบบจะไม่เดาผังบัญชีเองเด็ดขาด สิ่งที่ขาดคือ **การตั้งค่าทางบัญชี** ไม่ใช่ต้นทุนสินค้า — ให้ฝ่ายบัญชีตั้งคู่บัญชีของ movement type นี้ก่อน ยังไม่มีของเข้าสต๊อกและยังไม่มี lot ถูกสร้าง`,
      };
    case FISCAL_PERIOD_CLOSED:
      return {
        heading: 'งวดบัญชีของวันที่ที่มีผลนี้ไม่ได้เปิดอยู่',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${FISCAL_PERIOD_CLOSED} — การโพสต์เข้างวดที่ปิดแล้วคือการแก้งบที่ปิดไปแล้ว ไม่ใช่ความสะดวก การกดซ้ำจะได้คำตอบเดิมทุกครั้ง สิ่งที่ต้องทำคือแก้ **วันที่ที่มีผล** ให้อยู่ในงวดที่เปิดอยู่ หรือให้ฝ่ายบัญชีเปิดงวดนั้นก่อน — ยังไม่มีของเข้าสต๊อกและยังไม่มี lot ถูกสร้าง`,
      };
    case STORAGE_LOCATION_NOT_IN_PLANT:
      return {
        heading: 'ที่เก็บที่ระบุไม่ได้อยู่ในโรงงานที่เลือก',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${STORAGE_LOCATION_NOT_IN_PLANT} — รหัสที่เก็บซ้ำกันได้ข้ามโรงงาน ระบบจึงตรวจมันคู่กับโรงงานเสมอ ไม่ใช่ตรวจลำพัง ตรวจว่าเลือกโรงงานถูกแห่งหรือพิมพ์รหัสที่เก็บของอีกโรงงานมา — ยังไม่มีของเข้าสต๊อกและยังไม่มี lot ถูกสร้าง`,
      };
    case PRODUCT_NOT_ACTIVE:
      return {
        heading: 'สินค้าตัวนี้ยังรับเข้าในโรงงานนี้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_ACTIVE} — รับเข้าได้เฉพาะสินค้าที่สถานะเป็น ACTIVE และมีข้อมูลระดับโรงงานของโรงงานที่เลือกแล้ว ตรวจว่าเดินเส้นทางอนุมัติสินค้าจนจบหรือยัง และเลือกโรงงานถูกแห่งหรือไม่ — ยังไม่มีของเข้าสต๊อกและยังไม่มี lot ถูกสร้าง`,
      };
    case IDEMPOTENCY_KEY_REUSED:
      return {
        heading: 'คีย์กันซ้ำของคำขอนี้ถูกใช้ไปแล้วกับการรับเข้าอีกใบหนึ่ง',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${IDEMPOTENCY_KEY_REUSED} — คีย์เดียวกันเคยถูกส่งมาพร้อมข้อมูลที่ต่างจากครั้งนี้ เซิร์ฟเวอร์จึงปฏิเสธแทนที่จะเดาว่าจะนับเป็นใบไหน **การรับเข้าครั้งนี้ยังไม่เกิดขึ้น** และการกดซ้ำจะได้คำตอบเดิม — เปิดหน้ารายการเพื่อดูว่าใบก่อนหน้าเข้าไปแล้วหรือยัง แล้วเริ่มใบใหม่จากฟอร์มเปล่า ถ้ายังเห็นข้อความนี้ซ้ำอีก ให้แจ้งผู้ดูแลระบบ เพราะแปลว่าหน้าจอกำลังใช้คีย์ผิดใบ`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: GoodsReceiptFailure,
  context: GoodsReceiptFailureContext,
  plantCode: string | undefined,
): FailureCopy {
  const plant = plantCode === undefined || plantCode === '' ? 'โรงงานที่เลือกอยู่' : plantCode;
  // ความล้มเหลวของการบันทึกไม่เคยทำให้ของเข้าสต๊อกบางส่วน — ห้าแถวในธุรกรรมเดียว
  // ล้มจุดใดก็ไม่เหลืออะไรเลย ประโยคนี้จึงต้องอยู่กับ `create` ทุกกรณี
  const nothingPosted =
    context === 'create'
      ? ' ยังไม่มีของเข้าสต๊อก ไม่มี lot ถูกสร้าง และไม่มีเอกสารบัญชีถูกบันทึก'
      : '';

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: `โรงงาน ${plant} อยู่นอกขอบเขตที่คุณเข้าถึงได้`,
        text: `เซิร์ฟเวอร์ตอบ 403 — การรับเข้าเป็นของโรงงานใดโรงงานหนึ่งเสมอ และบัญชีนี้เข้าถึงโรงงานนั้นไม่ได้ (D-004) ไม่ใช่ว่าไม่มีข้อมูล — เลือกโรงงานอื่นที่คุณมีสิทธิ์ หรือขอสิทธิ์โรงงานนี้กับผู้ดูแลระบบ${nothingPosted}`,
      };
    case 'not-found':
      return {
        heading:
          context === 'detail' ? 'ไม่พบใบรับเข้าใบนี้ในมุมมองของคุณ' : 'ปลายทางของคำขอนี้ไม่มีอยู่',
        text: 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่าใบนี้ไม่มีอยู่จริง หรือมีอยู่แต่อยู่นอกโรงงานที่คุณมีสิทธิ์เห็น สองอย่างนี้ตอบเหมือนกันโดยตั้งใจ',
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ชนกับคำขออื่นที่ใช้คีย์กันซ้ำเดียวกัน',
          text: `เซิร์ฟเวอร์ตอบ 409 — คีย์กันซ้ำของคำขอนี้เคยถูกใช้กับข้อมูลที่ต่างออกไป คำขอจึงไม่ถูกดำเนินการ${nothingPosted}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingPosted}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text:
            context === 'create'
              ? `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น จำนวนไม่มากกว่า 0 ไม่มีคีย์กันซ้ำติดไปกับคำขอ หรือมีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของอย่าง quality_status ติดไปด้วย)${nothingPosted}`
              : 'เซิร์ฟเวอร์ตอบ 400 — คำขอที่ส่งไปไม่ตรงรูปแบบที่ API รับ',
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text:
          context === 'create'
            ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าการรับเข้าเกิดขึ้นหรือไม่** — เปิดหน้ารายการเพื่อดูด้วยตาก่อนว่าใบนี้เข้าไปแล้วหรือยัง ถ้ายัง ให้กดบันทึกซ้ำได้จากฟอร์มเดิม หน้าจอจะส่งคีย์กันซ้ำตัวเดิมไปให้ จึงไม่มีทางได้ของเข้าสองครั้ง'
            : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text:
          context === 'create'
            ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ กดบันทึกซ้ำได้จากฟอร์มเดิม หน้าจอจะส่งคีย์กันซ้ำตัวเดิมไปให้ ถ้าครั้งก่อนไปถึงแล้ว เซิร์ฟเวอร์จะคืนใบเดิม ไม่ใช่สร้างใบที่สอง`
            : `${failure.detail} — หน้าจอนี้จึงไม่มีใบรับเข้าจะแสดง และจะไม่เดาข้อมูลขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงใบรับเข้าที่อ่านได้ไม่ครบ ดีกว่าแสดงจำนวนหรือสถานะคุณภาพที่ไม่รู้ว่าถูกหรือผิด แล้วมีคนเบิกของที่ยังถูกกักอยู่ไปใช้',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function GoodsReceiptFailureNotice({
  failure,
  context,
  plantCode,
  onRetry,
}: GoodsReceiptFailureNoticeProps) {
  const copy = failureCopy(failure, context, plantCode);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;

  return (
    <div
      className="cap-notice"
      data-testid="goods-receipt-failure"
      data-failure={failure.status}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={failure.status === 'rule-violation' ? 'warning' : 'danger'}>
        {failure.status === 'rule-violation' ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="goods-receipt-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="goods-receipt-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
